import { formatMinor, type ChartAccount } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Input, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  chartQuery,
  openingBalancesQuery,
  openingFromGifi,
  setOpeningBalances,
} from '../../shared/api/ledger-queries.js';
import { dollarsToMinor, minorToDollars, parseGifiPaste } from './books-format.js';

/**
 * Opening balances: what every account held the day before the books start.
 *
 * Two ways in. Paste last year's balance sheet in GIFI codes (Schedule 100,
 * from the accountant's tax software or last year's T2) and each item lands
 * in its account; or type them. Either way a person sees every line before
 * it is saved, and any difference between debits and credits is shown, then
 * held in Opening balance equity, never quietly absorbed.
 */

interface Row {
  key: number;
  accountId: string;
  debit: string;
  credit: string;
  /** Where the row came from, when it came from a GIFI item. */
  note?: string;
}

const BALANCE_SHEET = new Set(['asset', 'liability', 'equity']);

const blankRow = (key: number): Row => ({ key, accountId: '', debit: '', credit: '' });

export function OpeningBalancesCard({
  organizationId,
  canEdit,
}: {
  organizationId: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const opening = useQuery(openingBalancesQuery(organizationId));
  const chart = useQuery(chartQuery(organizationId));
  const [editing, setEditing] = useState(false);
  const [asOf, setAsOf] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [paste, setPaste] = useState('');
  const [notices, setNotices] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState(1);

  const accounts = (chart.data ?? []).filter((account) => BALANCE_SHEET.has(account.accountType));

  const begin = () => {
    const current = opening.data;
    setAsOf(current?.asOf ?? `${String(new Date().getFullYear() - 1)}-12-31`);
    const existing = (current?.lines ?? [])
      .filter((line) => line.accountCode !== '3900')
      .map((line, index) => ({
        key: index + 1,
        accountId: line.accountId,
        debit: line.debitMinor === '0' ? '' : minorToDollars(line.debitMinor),
        credit: line.creditMinor === '0' ? '' : minorToDollars(line.creditMinor),
      }));
    setRows(existing.length === 0 ? [blankRow(1)] : existing);
    setNextKey(existing.length + 2);
    setNotices([]);
    setError(null);
    setEditing(true);
  };

  const place = useMutation({
    mutationFn: (items: { gifi: string; amountMinor: string }[]) =>
      openingFromGifi(organizationId, { items }),
    onSuccess: (placed, _items) => {
      const skipped = placed.filter((row) => row.skippedReason !== null);
      const used = placed.filter((row) => row.skippedReason === null);
      const fresh = used.map((row, index) => ({
        key: nextKey + index,
        accountId: row.accountId ?? '',
        debit: row.debitMinor === '0' ? '' : minorToDollars(row.debitMinor),
        credit: row.creditMinor === '0' ? '' : minorToDollars(row.creditMinor),
        note:
          row.accountId === null
            ? `GIFI ${row.gifiCode}: no account for it yet. Choose one.`
            : `GIFI ${row.gifiCode}`,
      }));
      setRows(fresh.length === 0 ? [blankRow(nextKey)] : fresh);
      setNextKey(nextKey + fresh.length + 1);
      setNotices((before) => [
        ...before,
        ...skipped.map((row) => `GIFI ${row.gifiCode} left out: ${row.skippedReason ?? ''}`),
      ]);
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'Those items could not be placed.'),
  });

  const readPaste = () => {
    const { items, unread } = parseGifiPaste(paste);
    setNotices(unread.map((line) => `Not read (no GIFI code and amount): ${line}`));
    if (items.length > 0) place.mutate(items);
  };

  const parsed = rows.map((row) => ({
    row,
    debit: dollarsToMinor(row.debit),
    credit: dollarsToMinor(row.credit),
  }));
  const unreadable = parsed.some(
    ({ debit, credit }) =>
      debit === null || credit === null || debit.startsWith('-') || credit.startsWith('-'),
  );
  const filled = parsed.filter(
    ({ debit, credit }) => (debit ?? '0') !== '0' || (credit ?? '0') !== '0',
  );
  const unplaced = filled.some(({ row }) => row.accountId === '');
  const debits = filled.reduce((sum, { debit }) => sum + BigInt(debit ?? '0'), 0n);
  const credits = filled.reduce((sum, { credit }) => sum + BigInt(credit ?? '0'), 0n);
  const difference = debits - credits;

  const save = useMutation({
    mutationFn: () =>
      setOpeningBalances(organizationId, {
        asOf,
        lines: filled.map(({ row, debit, credit }) => ({
          accountId: row.accountId,
          debitMinor: debit ?? '0',
          creditMinor: credit ?? '0',
        })),
      }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['ledger', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['reports', organizationId] });
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The opening balances were not saved.'),
  });

  const update = (key: number, change: Partial<Row>) =>
    setRows((before) => before.map((row) => (row.key === key ? { ...row, ...change } : row)));

  const current = opening.data;
  const held = current?.lines.find((line) => line.accountCode === '3900');

  return (
    <Card>
      <CardHeader
        title="Opening balances"
        description={
          current === undefined
            ? 'Reading…'
            : current.asOf === null
              ? 'Not set. The books count from the first thing imported, so balances are movements, not what the bank shows. Enter what each account held the day before the books start.'
              : `The books start after ${current.asOf}.${held === undefined ? ' Debits equal credits.' : ` ${formatMinor(held.creditMinor !== '0' ? held.creditMinor : held.debitMinor)} is still to find: it waits in Opening balance equity.`}`
        }
      />
      <CardBody className="flex flex-col gap-4">
        {error === null ? null : <Alert tone="danger">{error}</Alert>}

        {!editing ? (
          <>
            {opening.isPending ? (
              <Skeleton className="h-24 w-full" />
            ) : (current?.lines.length ?? 0) > 0 ? (
              <OpeningTable lines={current?.lines ?? []} />
            ) : null}
            {canEdit ? (
              <div>
                <Button variant={current?.asOf === null ? 'primary' : 'secondary'} onClick={begin}>
                  {current?.asOf === null ? 'Enter opening balances' : 'Change opening balances'}
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm">
              <span className="text-content-muted">Balances at the end of</span>
              <input
                type="date"
                value={asOf}
                onChange={(event) => setAsOf(event.target.value)}
                className="rounded-[var(--radius-control)] border border-border bg-surface px-2 py-1"
              />
              <span className="text-content-muted">(usually last year-end)</span>
            </label>

            <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-border p-3">
              <p className="text-sm">
                <span className="font-medium">From last year's return.</span>{' '}
                <span className="text-content-muted">
                  Paste Schedule 100 (the balance sheet in GIFI codes) from the accountant's tax
                  software or last year's T2: a code and an amount on each line.
                </span>
              </p>
              <textarea
                value={paste}
                onChange={(event) => setPaste(event.target.value)}
                rows={4}
                placeholder={'1002  5,000.00\n2707  1,200.00\n3600  13,700.00'}
                className="w-full rounded-[var(--radius-control)] border border-border bg-surface px-2 py-1 font-mono text-sm"
              />
              <div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={readPaste}
                  isLoading={place.isPending}
                  disabled={paste.trim() === ''}
                >
                  Place in accounts
                </Button>
              </div>
            </div>

            {notices.length === 0 ? null : (
              <ul className="flex flex-col gap-1 text-xs text-content-muted">
                {notices.map((notice) => (
                  <li key={notice}>{notice}</li>
                ))}
              </ul>
            )}

            <table className="w-full text-sm tabular">
              <thead>
                <tr className="text-left text-xs text-content-muted">
                  <th className="py-1 font-medium">Account</th>
                  <th className="w-36 py-1 text-right font-medium">Debit</th>
                  <th className="w-36 py-1 text-right font-medium">Credit</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td className="py-1 pr-2">
                      <AccountSelect
                        accounts={accounts}
                        value={row.accountId}
                        onChange={(accountId) => update(row.key, { accountId })}
                      />
                      {row.note === undefined ? null : (
                        <p
                          className={[
                            'mt-0.5 text-xs',
                            row.accountId === '' ? 'text-warning' : 'text-content-muted',
                          ].join(' ')}
                        >
                          {row.note}
                        </p>
                      )}
                    </td>
                    <td className="py-1 pl-2">
                      <Input
                        aria-label="Debit"
                        inputMode="decimal"
                        className="text-right"
                        value={row.debit}
                        hasError={dollarsToMinor(row.debit) === null}
                        onChange={(event) =>
                          update(row.key, { debit: event.target.value, credit: '' })
                        }
                      />
                    </td>
                    <td className="py-1 pl-2">
                      <Input
                        aria-label="Credit"
                        inputMode="decimal"
                        className="text-right"
                        value={row.credit}
                        hasError={dollarsToMinor(row.credit) === null}
                        onChange={(event) =>
                          update(row.key, { credit: event.target.value, debit: '' })
                        }
                      />
                    </td>
                    <td className="py-1 text-right">
                      <button
                        type="button"
                        aria-label="Remove line"
                        className="text-content-muted hover:text-danger"
                        onClick={() =>
                          setRows((before) => before.filter((other) => other.key !== row.key))
                        }
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-border font-medium">
                  <td className="py-1">
                    <button
                      type="button"
                      className="text-sm text-brand-700 hover:underline"
                      onClick={() => {
                        setRows((before) => [...before, blankRow(nextKey)]);
                        setNextKey(nextKey + 1);
                      }}
                    >
                      Add an account
                    </button>
                  </td>
                  <td className="py-1 text-right">{formatMinor(debits.toString())}</td>
                  <td className="py-1 text-right">{formatMinor(credits.toString())}</td>
                  <td />
                </tr>
              </tbody>
            </table>

            <p
              className={['text-sm', difference === 0n ? 'text-success' : 'text-warning'].join(' ')}
            >
              {difference === 0n
                ? 'Debits equal credits. It balances.'
                : `Out by ${formatMinor((difference < 0n ? -difference : difference).toString())}. It can be saved: the difference waits in Opening balance equity, in plain sight, until it is found.`}
            </p>

            <div className="flex gap-2">
              <Button
                onClick={() => save.mutate()}
                isLoading={save.isPending}
                disabled={asOf === '' || unreadable || unplaced || filled.length === 0}
              >
                Save opening balances
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
            {unplaced ? (
              <p className="text-xs text-warning">Every amount needs an account before saving.</p>
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}

function AccountSelect({
  accounts,
  value,
  onChange,
}: {
  accounts: readonly ChartAccount[];
  value: string;
  onChange: (accountId: string) => void;
}) {
  return (
    <select
      aria-label="Account"
      className="w-full rounded-[var(--radius-control)] border border-border bg-surface px-2 py-1 text-sm"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Choose an account…</option>
      {(['asset', 'liability', 'equity'] as const).map((type) => (
        <optgroup
          key={type}
          label={type === 'asset' ? 'Assets' : type === 'liability' ? 'Liabilities' : 'Equity'}
        >
          {accounts
            .filter((account) => account.accountType === type)
            .map((account) => (
              <option key={account.id} value={account.id}>
                {account.code ?? ''} {account.name}
                {account.gifiCode === null ? '' : ` (GIFI ${account.gifiCode})`}
              </option>
            ))}
        </optgroup>
      ))}
    </select>
  );
}

function OpeningTable({
  lines,
}: {
  lines: readonly {
    accountId: string;
    accountCode: string | null;
    accountName: string;
    debitMinor: string;
    creditMinor: string;
  }[];
}) {
  return (
    <table className="w-full text-sm tabular">
      <thead>
        <tr className="text-left text-xs text-content-muted">
          <th className="py-1 font-medium">Account</th>
          <th className="py-1 text-right font-medium">Debit</th>
          <th className="py-1 text-right font-medium">Credit</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr key={line.accountId}>
            <td className="py-0.5">
              <span className="text-content-muted">{line.accountCode}</span> {line.accountName}
            </td>
            <td className="py-0.5 text-right">
              {line.debitMinor === '0' ? '' : formatMinor(line.debitMinor)}
            </td>
            <td className="py-0.5 text-right">
              {line.creditMinor === '0' ? '' : formatMinor(line.creditMinor)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
